import React, { useState, useEffect } from 'react';
import { AdminPageShell } from './_components/AdminPageShell';
import { HealthStrip } from './_components/HealthStrip';
import { ManagementGrid } from './_components/ManagementGrid';
import { apiFetch } from '@/lib/apiFetch';

interface PipelineStats {
  stuckTotal: number;
  needsReviewTotal: number;
}

interface WorkerStatus {
  status: 'online' | 'offline';
  minutesAgo: number | null;
  jobsActive: number;
}

const AdminPage: React.FC = () => {
  const [sourceCount, setSourceCount] = useState(0);
  const [pipelineStats, setPipelineStats] = useState<PipelineStats>({
    stuckTotal: 0,
    needsReviewTotal: 0,
  });
  const [workerStatus, setWorkerStatus] = useState<WorkerStatus>({
    status: 'offline',
    minutesAgo: null,
    jobsActive: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      try {
        const [srcRes, pipeRes, workerRes] = await Promise.all([
          apiFetch('/api/source-health'),
          apiFetch('/api/pipeline-reconcile'),
          apiFetch('/api/worker-heartbeat'),
        ]);
        if (cancelled) return;

        if (srcRes.ok) {
          const srcJson = await srcRes.json();
          setSourceCount(Array.isArray(srcJson.data) ? srcJson.data.length : 0);
        }

        if (pipeRes.ok) {
          const pipeJson = await pipeRes.json();
          const rows =
            pipeJson.data?.jurisdictions ?? pipeJson.data ?? pipeJson ?? [];
          let stuck = 0;
          let needsReview = 0;
          for (const row of rows) {
            stuck += Number(row.no_extracted_text ?? 0);
            stuck += Number(row.extracted_no_chunks ?? 0);
            stuck += Number(row.chunks_not_embedded ?? 0);
            stuck += Number(row.minutes_no_attendees ?? 0);
            stuck += Number(row.minutes_no_motions ?? 0);
            stuck += Number(row.minutes_no_financial ?? 0);
            needsReview += Number(row.minutes_no_attendees_needs_review ?? 0);
            needsReview += Number(row.minutes_no_motions_needs_review ?? 0);
            needsReview += Number(row.minutes_no_financial_needs_review ?? 0);
          }
          setPipelineStats({
            stuckTotal: stuck,
            needsReviewTotal: needsReview,
          });
        }

        if (workerRes.ok) {
          const workerJson = await workerRes.json();
          const w = workerJson.data;
          if (w) {
            setWorkerStatus({
              status: w.status ?? 'offline',
              minutesAgo: w.minutes_ago ?? null,
              jobsActive: w.jobs_active ?? 0,
            });
          }
        }
      } catch {
        /* ignore */
      }
      if (!cancelled) setLoading(false);
    };
    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AdminPageShell title="Dashboard">
      <HealthStrip
        sourceCount={sourceCount}
        stuckTotal={pipelineStats.stuckTotal}
        needsReviewTotal={pipelineStats.needsReviewTotal}
        workerStatus={workerStatus}
        loading={loading}
      />
      <div className="mt-8">
        <ManagementGrid />
      </div>
    </AdminPageShell>
  );
};

export { AdminPage };
export default AdminPage;
